import { Option } from '../../components/ui/MultiSelect/MultiSelect';

/**
 * Filtros de la tabla de cargas.
 *
 * ⚠️ **Oficinas, transportistas y modos guardan IDs, no nombres.** Antes guardaban el nombre porque
 * el filtrado ocurría en el navegador comparando textos; ahora lo hace el backend, que filtra por
 * `load_office_id`, `carrier_id` y `load_mode_id`. Mandar nombres devolvería cero resultados sin
 * ningún error visible.
 *
 * 📌 `operationTypes` sí guarda el valor de texto: `operation_type` es una columna de texto libre en
 * `loads`, no una llave a un catálogo.
 */
export interface LoadsFilters {
  offices: string[];
  carriers: string[];
  operationTypes: string[];
  modes: string[];
  months: string[]; // formato 'YYYY-MM'
  /**
   * Ids de cliente (19-oct-2026): *"nos falta uno de clientes si tienen mas de un cliente asignado"*. El backend ya lo
   * aceptaba (`clients`) y lo **intersecta** con los asignados, así que elegir uno no abre nada.
   */
  clients: string[];
}

export const EMPTY_FILTERS: LoadsFilters = {
  offices: [],
  carriers: [],
  operationTypes: [],
  modes: [],
  months: [],
  clients: [],
};

export const activeFilterCount = (f: LoadsFilters): number =>
  f.offices.length +
  f.carriers.length +
  f.operationTypes.length +
  f.modes.length +
  f.months.length +
  f.clients.length;

/** Etiqueta legible de un mes `YYYY-MM`. */
export const monthLabel = (key: string): string => {
  const [y, m] = key.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

/** Opciones de cada menú del modal de filtros. */
export interface LoadsFilterOptions {
  offices: Option[];
  carriers: Option[];
  operationTypes: Option[];
  modes: Option[];
  months: Option[];
  /** Los clientes asignados que tienen cargas, de `/loads-filter-options`. El modal muestra el filtro si son más de uno. */
  clients: Option[];
}

export const EMPTY_FILTER_OPTIONS: LoadsFilterOptions = {
  offices: [],
  carriers: [],
  operationTypes: [],
  modes: [],
  months: [],
  clients: [],
};

/** ¿Se ofrece el filtro de clientes? Solo con más de uno: con uno solo, filtrar por él no cambia nada. */
export const showsClientFilter = (options: LoadsFilterOptions): boolean => options.clients.length > 1;

/**
 * Traduce los filtros a parámetros de consulta con los nombres que espera el backend.
 *
 * 📌 Vive aquí, junto a la definición de los filtros, para que agregar uno obligue a mirar cómo se
 * manda. Cuando estaba repartido entre el hook y la pantalla era fácil agregar un filtro que se
 * aplicaba en el navegador y nunca viajaba.
 */
export const filtersToParams = (f: LoadsFilters): Record<string, string[] | undefined> => ({
  offices: f.offices.length ? f.offices : undefined,
  carriers: f.carriers.length ? f.carriers : undefined,
  operationTypes: f.operationTypes.length ? f.operationTypes : undefined,
  modes: f.modes.length ? f.modes : undefined,
  months: f.months.length ? f.months : undefined,
  clients: f.clients.length ? f.clients : undefined,
});

/** Un filtro activo, como lo lista el modal de los reportes. */
export interface ActiveFilter {
  label: string;
  values: string[];
}

/**
 * Los filtros que van a viajar con el reporte, en palabras (PENDIENTES_TERRESTRE, punto 8; 13-oct-2026).
 *
 * > *"Modal antes de la descarga donde muestre los filtros activos actuales"*
 *
 * 🔑 Se arma con **lo mismo que se manda**: la búsqueda ya con su espera (`debouncedSearch`), los estatus de las
 * tarjetas, la pestaña de críticas y los del modal de filtros. Si se armara con lo que hay en la caja de búsqueda, el
 * modal podría listar una búsqueda que todavía no viajó.
 *
 * 📌 Mismas etiquetas que la línea de filtros que el backend escribe dentro del Excel (`describeLoadFilters`), para que
 * el modal y el archivo digan lo mismo. Oficinas, transportistas y modos viajan como ids: aquí se muestran con su nombre.
 * El mes es el de **creación** de la carga (*"la fecha aplica a la de la creación de la carga"*), y así se dice.
 */
export const describeActiveFilters = (input: {
  search?: string;
  selectedStatuses?: string[];
  critical?: boolean;
  filters: LoadsFilters;
  options: LoadsFilterOptions;
}): ActiveFilter[] => {
  const { filters, options } = input;
  const nameOf = (list: Option[]) => (value: string) => list.find((o) => o.value === value)?.label ?? value;
  const out: ActiveFilter[] = [];
  const add = (label: string, values: string[]) => {
    if (values.length) out.push({ label, values });
  };

  const search = (input.search || '').trim();
  if (search) add('Search', [`"${search}"`]);
  if (input.critical) add('Tab', ['Critical Loads']);
  add('Status', input.selectedStatuses ?? []);
  add('Client', filters.clients.map(nameOf(options.clients)));
  add('Carrier', filters.carriers.map(nameOf(options.carriers)));
  add('Office', filters.offices.map(nameOf(options.offices)));
  add('Mode', filters.modes.map(nameOf(options.modes)));
  add('Operation type', filters.operationTypes);
  add('Load created in', filters.months.map(monthLabel));
  return out;
};
